import styled from 'styled-components';
import { ReactComponent as DownstreamIcon } from '@elementary/common-ui/assets/stream-down.svg';
import { ReactComponent as UpstreamIcon } from '@elementary/common-ui/assets/stream-up.svg';
import { ReactComponent as StreamIcon } from '@elementary/common-ui/assets/stream.svg';
import { Button, Flex } from '@elementary/common-ui/components';
import { Theme, Tooltip } from '@mui/material';
import { tileStyles } from '../../Button/styles';
import type { DagDepthBoundary, HopLimit } from '../types';
import HopSelector from './HopSelector';

const TRACK_PADDING = 2;

// With a track color the direction toggles sit flush on one segmented
// track; without it they stay separate tiles in the row.
const StreamTrack = styled.div`
  display: contents;

  ${({ theme }) => {
    const { track, radius } = theme.tokens.components.lineage.streamToggle;
    return (
      track &&
      `
      display: flex;
      align-items: center;
      box-sizing: border-box;
      height: 32px;
      padding: 0 ${TRACK_PADDING}px;
      border: 1px solid ${track};
      border-radius: ${radius};
    `
    );
  }}
`;

const segmentStyles = (theme: Theme, active: boolean) => {
  const {
    track,
    segment,
    hoverSegment,
    selectedSegment,
    icon,
    selectedIcon,
    disabledIcon,
    radius,
  } = theme.tokens.components.lineage.streamToggle;
  if (!track) return '';
  return `
    width: 32px;
    height: 24px;
    padding: 0;
    border-radius: calc(${radius} - ${TRACK_PADDING}px);
    background: ${active ? selectedSegment : segment};

    :hover,
    :active:not(:disabled) {
      background: ${active ? selectedSegment : hoverSegment};
    }

    :disabled {
      background: ${segment};
    }

    ${
      icon
        ? `
    svg path {
      fill: ${active ? selectedIcon : icon};
    }

    :disabled svg path {
      fill: ${disabledIcon};
    }
    `
        : ''
    }
  `;
};

const StreamButton = styled(Button)<{ $active: boolean }>`
  width: 44px;
  min-width: 0;
  background: ${({ theme, $active }) =>
    $active ? theme.palette.surface.inverse : theme.palette.surface.muted};

  :hover {
    background: ${({ theme, $active }) =>
      $active ? theme.palette.surface.inverse : theme.palette.surface.strong};
  }

  path {
    fill: ${({ theme, $active }) =>
      $active ? theme.palette.icon.inverse : theme.palette.icon.primary};
  }

  ${({ theme, $active }) =>
    tileStyles(theme, {
      background: theme.palette.surface.muted,
      hoverBackground: $active
        ? theme.palette.surface.inverse
        : theme.palette.surface.strong,
    })}

  ${({ theme, $active }) => segmentStyles(theme, $active)}
`;

type Props = {
  hopLimit?: { downstream?: number; upstream?: number };
  onHopLimitChange: (val?: HopLimit) => void;
  dagDepthBoundary?: DagDepthBoundary;
};

const StreamSelector = ({
  hopLimit,
  onHopLimitChange,
  dagDepthBoundary,
}: Props) => {
  const upstreamValue = (
    hopLimit?.upstream === undefined
      ? dagDepthBoundary?.incomingDepth || 0
      : hopLimit.upstream
  ).toString();

  const downstreamValue = (
    hopLimit?.downstream === undefined
      ? dagDepthBoundary?.outgoingDepth || 0
      : hopLimit.downstream
  ).toString();

  const handleUpstreamHopChange = (val: string) => {
    if (val !== '') {
      onHopLimitChange({ ...hopLimit, upstream: Number(val) });
    }
  };

  const handleDownstreamHopChange = (val: string) => {
    if (val !== '') {
      onHopLimitChange({ ...hopLimit, downstream: Number(val) });
    }
  };

  const hasUpstreamAndDownstream =
    dagDepthBoundary?.outgoingDepth !== 0 &&
    dagDepthBoundary?.incomingDepth !== 0;

  return (
    <Flex sx={{ gap: 1, alignItems: 'center' }}>
      <HopSelector
        value={upstreamValue}
        onChange={handleUpstreamHopChange}
        maxValue={dagDepthBoundary?.incomingDepth || 0}
      />

      <StreamTrack>
        <Tooltip title="Upstream of selected nodes">
          <StreamButton
            $active={
              hopLimit?.downstream === 0 &&
              hopLimit?.upstream !== 0 &&
              hasUpstreamAndDownstream
            }
            disabled={!hasUpstreamAndDownstream}
            onClick={() =>
              onHopLimitChange({
                upstream: hopLimit?.upstream || dagDepthBoundary?.incomingDepth,
                downstream: 0,
              })
            }
          >
            <UpstreamIcon />
          </StreamButton>
        </Tooltip>

        <Tooltip title="Upstream & downstream of selected nodes">
          <StreamButton
            $active={
              (hopLimit?.upstream !== 0 && hopLimit?.downstream !== 0) ||
              (hopLimit?.upstream === 0 && hopLimit?.downstream === 0) ||
              !hasUpstreamAndDownstream
            }
            onClick={() =>
              onHopLimitChange({
                upstream: hopLimit?.upstream || dagDepthBoundary?.incomingDepth,
                downstream:
                  hopLimit?.downstream || dagDepthBoundary?.outgoingDepth,
              })
            }
          >
            <StreamIcon />
          </StreamButton>
        </Tooltip>

        <Tooltip title="Downstream of selected nodes">
          <StreamButton
            $active={
              hopLimit?.upstream === 0 &&
              hopLimit?.downstream !== 0 &&
              hasUpstreamAndDownstream
            }
            disabled={!hasUpstreamAndDownstream}
            onClick={() =>
              onHopLimitChange({
                upstream: 0,
                downstream:
                  hopLimit?.downstream || dagDepthBoundary?.outgoingDepth,
              })
            }
          >
            <DownstreamIcon />
          </StreamButton>
        </Tooltip>
      </StreamTrack>

      <HopSelector
        value={downstreamValue}
        onChange={handleDownstreamHopChange}
        maxValue={dagDepthBoundary?.outgoingDepth || 0}
      />
    </Flex>
  );
};

export default StreamSelector;
